// Compute graph representation and execution
//
// Contains low-level functions & types for constructing & executing
// a compute graph.
//
// User (UI) code generally should not need anything other than the Node
// type in this file.

import * as _ from 'lodash';

import * as Model from './model/types';
import {DEBUG_ATTACH_TO_GLOBAL_THIS} from './utils/constants';
import * as CGTypes from './types';
import * as HL from './hl';

import {ForwardGraph, ForwardOp} from './engine/forwardGraph';

///// Node & Op definitions

// Nodes represent values, Ops represent operations. An op
// always has a single output node of nodeType 'output', and may
// consume many inputs. Many ops may consume the same node.
//
// When constructing a graph by calling the various operations in
// ops.ts, we only store backward references (ops refer to their
// inputs, not the other way around).

export const isVoidNode = (
  node: Model.NodeOrVoidNode
): node is Model.VoidNode => {
  return node.nodeType === 'void';
};

const registeredOps: {
  [name: string]: CGTypes.OpDef;
} = {};

export const allGlobalOps = () => registeredOps;

export const opDefIsLowLevel = (
  opDef: CGTypes.OpDef
): opDef is CGTypes.OpDefLowLevel => {
  return (opDef as CGTypes.OpDefLowLevel).resolver != null;
};

export const opDefIsWeave = (
  opDef: CGTypes.OpDef
): opDef is CGTypes.OpDefWeave => {
  return (opDef as CGTypes.OpDefWeave).body != null;
};

export const opDefIsGeneratedWeave = (
  opDef: CGTypes.OpDef
): opDef is CGTypes.OpDefGeneratedWeave => {
  return (opDef as CGTypes.OpDefGeneratedWeave).expansion != null;
};

export const getLowLevelOp = (name: string): CGTypes.OpDefLowLevel => {
  const opDef = registeredOps[name];
  if (!opDefIsLowLevel(opDef)) {
    throw new Error('expected low-level op for: ' + name);
  }
  return opDef;
};

// Useful for console debugging
if (typeof globalThis !== 'undefined' && DEBUG_ATTACH_TO_GLOBAL_THIS) {
  (globalThis as any).op = (opName: string) => {
    const opDef = registeredOps[opName];
    if (!opDef) {
      throw new TypeError(`unknown op: '${opName}'`);
    }
    return (...args: any[]) => {
      // Map pos args to input object keys by definition order
      const argObj = Object.keys(opDef.inputTypes).reduce((obj, key, index) => {
        let posArg = args[index];

        if (typeof posArg === 'undefined') {
          throw new TypeError(`missing value for input '${key}'`);
        } else if (typeof posArg !== 'object') {
          // Naively wrap js non-object primitives as const node
          posArg = {
            nodeType: 'const',
            type: typeof posArg,
            val: posArg,
          };
        }

        (obj as any)[key] = posArg;
        return obj;
      }, {});
      if (opDefIsLowLevel(opDef)) {
        return opDef.op(argObj as any);
      }
      return null;
    };
  };
}

const defaultCachePolicy: CGTypes.OpCachePolicy = {
  ttlSeconds: 30,
};

function registerOp<IT extends CGTypes.InputTypes>(
  name: string,
  inputTypes: CGTypes.InputTypes,
  outputType: Model.Type | CGTypes.TypeFn<IT, Model.Type>,
  renderType: CGTypes.OpRenderInfo,
  op: CGTypes.OpFn,
  resolver: CGTypes.OpResolverFn,
  description: string,
  argDescriptions: {
    [key: string]: string;
  },
  returnValueDescription: string,
  resolveOutputType?: CGTypes.RefineNodeFn,
  hidden?: boolean,
  kind?: string,
  cachePolicy: CGTypes.OpCachePolicy = defaultCachePolicy
) {
  if (registeredOps[name] != null) {
    throw new Error(
      `programming error: double registered op ${name} (is this a duplicate?)`
    );
  }
  registeredOps[name] = {
    name,
    inputTypes,
    outputType: outputType as
      | Model.Type
      | CGTypes.TypeFn<CGTypes.InputTypes, Model.Type>,
    renderInfo: renderType,
    op,
    resolver,
    refineNode: resolveOutputType,
    hidden,
    kind,
    cachePolicy,
    description,
    argDescriptions,
    returnValueDescription,
  };
}

export function registerGeneratedWeaveOp<IT extends CGTypes.InputTypes>(opts: {
  name: string;
  inputTypes: CGTypes.InputTypes;
  outputType: Model.Type | CGTypes.TypeFn<IT, Model.Type>;
  expansionFn: CGTypes.ExpansionFunction;
}) {
  const {name, inputTypes, outputType, expansionFn} = opts;
  registeredOps[name] = {
    name,
    inputTypes,
    outputType: outputType as
      | Model.Type
      | CGTypes.TypeFn<CGTypes.InputTypes, Model.Type>,
    renderInfo: {
      type: 'chain',
    },
    expansion: expansionFn,
    // TODO: allow these to be passed in by panels. Note:
    // keeping this excluded for now as panel ops will likely
    // change this soon.
    description: '',
    argDescriptions: {},
    returnValueDescription: '',
  };
}

export function makeOp<
  ON extends string,
  RT extends Model.Type,
  I extends CGTypes.InputTypes
>(opts: {
  name: ON;
  argTypes: I;
  returnType: RT | CGTypes.TypeFn<I, RT>;
  renderInfo?: CGTypes.OpRenderInfo;
  resolver?: CGTypes.OpResolverFn;
  resolveOutputType?: CGTypes.RefineNodeFn;
  hidden?: boolean;
  kind?: string;
  cachePolicy?: CGTypes.OpCachePolicy;
  description?: string;
  argDescriptions?: {
    [key: string]: string;
  };
  returnValueDescription?: string;
}) {
  const {
    name: opName,
    argTypes: inputTypes,
    returnType,
    renderInfo,
    resolver,
    resolveOutputType,
    hidden,
    kind,
    cachePolicy,
  } = opts;
  let {description, argDescriptions, returnValueDescription} = opts;

  if (description == null) {
    if (!hidden) {
      throw new Error('op description is required for non-hidden ops');
    } else {
      description = 'HIDDEN OP';
    }
  }

  if (argDescriptions == null) {
    if (!hidden) {
      throw new Error('op argDescriptions is required for non-hidden ops');
    } else {
      argDescriptions = _.mapValues(inputTypes, () => 'HIDDEN OP');
    }
  }

  if (returnValueDescription == null) {
    if (!hidden) {
      throw new Error(
        'op returnValueDescription is required for non-hidden ops'
      );
    } else {
      returnValueDescription = 'HIDDEN OP';
    }
  }

  const opFn = (inputs: CGTypes.OpInputNodes<I>): Model.OutputNode<RT> => {
    const type =
      typeof returnType === 'function' ? returnType(inputs) : returnType;
    const result = {
      nodeType: 'output',
      type,
      fromOp: {
        name: opName,
        inputs,
      },
    };
    return result as Model.OutputNode<RT>;
  };
  const res =
    resolver ??
    ((inputs: any) => {
      throw new Error('resolver not implemented for ' + opName);
    });
  registerOp(
    opName,
    inputTypes,
    returnType as any,
    renderInfo || {type: 'chain'},
    opFn,
    res,
    description,
    argDescriptions,
    returnValueDescription,
    resolveOutputType,
    hidden,
    kind,
    cachePolicy
  );
  return opFn;
}

// TODO: After we workout how linking and type names work, fill
// this function in with better logic.
export function docType(
  typeId: Model.TypeID,
  options: {plural?: boolean} = {}
): string {
  const plural = options?.plural ?? false;

  let text: string = typeId;
  if (
    // Hardcoded list of types which are W&B specific (likely would not be
    // included in an open source version)
    [
      'report',
      'artifact',
      'artifactVersion',
      'artifactType',
      'run',
      'entity',
      'project',
      'org',
      'user',
      'image-file',
      'video-file',
      'audio-file',
      'html-file',
      'bokeh-file',
      'object3D-file',
      'molecule-file',
      'table',
      'joined-table',
      'partitioned-table',
      'file',
      'dir',
    ].indexOf(typeId) >= 0
  ) {
    text = `W&B ${typeId}`;
  } else {
    text = `${typeId}`;
  }

  if (plural) {
    // if there are more cases we should factor this out into a map
    if (text === 'W&B Entity') {
      text = 'W&B Entities';
    } else {
      text += 's';
    }
  }

  // Just emphasizing for now
  const linked = `_${text}_`; // `[${text}]()`;
  return linked;
}

export function constNodeIsType<T extends Model.Type>(
  constNode: Model.ConstNode<Model.Type>,
  type: T
): constNode is Model.ConstNode<T> {
  return Model.isAssignableTo(constNode.type, type);
}

export function voidNode(): Model.VoidNode {
  return {nodeType: 'void', type: 'invalid'};
}

/** Notice: all new uses of varNode should use the one in consts.ts! */
export function varNode<T extends Model.Type>(
  type: T,
  varName: string
): Model.VarNode<T> {
  return {
    nodeType: 'var',
    type,
    varName,
  };
}

export function getOpDef(name: string): CGTypes.OpDef {
  const opDef = registeredOps[name];
  if (opDef == null) {
    throw new Error('op not registered: ' + name);
  }
  return opDef;
}

export function getBinaryOpDefsByRepr(repr: string): CGTypes.OpDef[] {
  return Object.values(registeredOps).filter(
    opDef =>
      opDef.renderInfo.type === 'binary' && opDef.renderInfo.repr === repr
  );
}

export function getOpDefsByDisplayName(displayName: string) {
  return Object.values(registeredOps).filter(
    opDef => HL.opDisplayName({name: opDef.name}) === displayName
  );
}

export function forwardOpInputs(
  fg: ForwardGraph,
  fo: ForwardOp
): {[x: string]: any} {
  return _.mapValues(fo.op.inputs, inputNode => {
    if (inputNode.nodeType === 'output') {
      const inputForwardOp = fg.getOp(inputNode.fromOp)!;
      return inputForwardOp.outputNode.result;
    } else if (inputNode.nodeType === 'const') {
      return inputNode.val;
    } else if (inputNode.nodeType === 'var') {
      return undefined;
    }
  });
}

// :( :(, leaking our language/resolver implementation details into
// low-level graph.ts :(.
export function unwrapTaggedValues(result: any): any {
  // Can't use Types.isConcreteTaggedValue here because we don't import Types
  if (result != null && result._tag !== undefined) {
    return unwrapTaggedValues(result._value);
  }
  if (_.isArray(result)) {
    return result.map((row: any) => unwrapTaggedValues(row));
  } else if (_.isObject(result) && !(result instanceof Date)) {
    return _.mapValues(result, (v: any) => unwrapTaggedValues(v));
  }
  return result;
}
